import { isSpatiusProvider } from "@/hooks/spatius-primary-session-policy";
import { createSpatiusStartupTiming } from "@/lib/spatius-startup-timing";
import { AvatarSDK, LogLevel as AvatarSdkLogLevel, DrivingServiceMode } from "@spatius/avatarkit";
import { useEffect, useRef, useState } from "react";

/**
 * Starts existing SDK warmup without creating a room. SDK initialization may
 * download WASM/template resources. The return value gates speculative work
 * only; Start must never await warmup. Failure releases that gate and leaves
 * session-time initialization authoritative.
 */
export function useSpatiusSdkPreinit(enabled = true): boolean {
  const warmup = useRef<Promise<void> | null>(null);
  const [settled, setSettled] = useState(false);
  const provider = (process.env.NEXT_PUBLIC_AVATAR_PROVIDER || "anam").trim().toLowerCase();
  const appId = process.env.NEXT_PUBLIC_SPATIUS_APP_ID;
  const shouldWarmup = enabled && isSpatiusProvider(provider) && Boolean(appId);
  const initialized = Boolean(
    (AvatarSDK as typeof AvatarSDK & { isInitialized?: boolean }).isInitialized
  );

  useEffect(() => {
    if (!shouldWarmup || !appId || initialized) return;
    let observing = true;
    if (!warmup.current) {
      const timing = createSpatiusStartupTiming({ source: "page_warmup" });
      timing.start("sdk_initialization");
      warmup.current = AvatarSDK.initialize(appId, {
        drivingServiceMode: DrivingServiceMode.rtc,
        logLevel:
          process.env.NEXT_PUBLIC_ENABLE_RTC_SDK_LOGS === "1" ? undefined : AvatarSdkLogLevel.off,
        region: process.env.NEXT_PUBLIC_SPATIUS_REGION || "us-west",
      } as Parameters<typeof AvatarSDK.initialize>[1] & { region?: string }).then(
        () => timing.finish("completed"),
        (error: unknown) => timing.finish("failed", error)
      );
    }
    void warmup.current.then(() => {
      if (observing) setSettled(true);
    });
    return () => {
      observing = false;
    };
  }, [shouldWarmup, appId, initialized]);
  return !shouldWarmup || initialized || settled;
}
